import qs from 'qs';
import { SagaIterator } from 'redux-saga';
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { httpDelete as httpDeleteOld } from 'src/utils';
import { httpGet, httpPost, httpDelete, ApiResponse } from 'src/api';
import { Response } from 'src/domains/global/types';
import { setError, setResponse, startQuery, reset } from './actions';
import { reset as resetFilter, setExpression } from './filter/actions';
import { removeGrid, addGrid, addSnackbar } from 'src/domains/global/actions';
import { GetBaseUrl } from 'src/domains/global/selectors';
import { conf as globalConfig, Schema, Resource } from 'src/config';
import * as types from './types'
import { Entities, Value } from 'src/domains/explorer/types';
import { SelectEntities } from 'src/domains/explorer/selectors';
import { GetDefault, SelectRequest } from './selectors';
import { GetGrids, GetGrid } from '../global/selectors';
import { getResponsePage, getSortQueryStr, getInitialRequest, parseSortQueryStr, parseAttrubutes, downloadBlob } from './utils';
import { Parser } from './filter/parser';
import { Expression } from './filter/types';
import { isTransient } from 'src/domains/explorer/utils';
import _, { Dictionary } from 'lodash';
import { hasText } from 'src/utils';
import { parse } from 'qs';


function* startWatchers(): SagaIterator {
    yield takeEvery(types.LIST_START_QUERY, startQueryWorker);
    yield takeEvery(types.LIST_START_INITIAL_QUERY, startInitialQueryWorker);
    yield takeEvery(types.LIST_START_EXISTING_QUERY, startExistingQueryWorker);
    yield takeEvery(types.ENTITY_START_DELETE, startDeleteWorker);
    yield takeLatest(types.LIST_SET_PAGE, startPageWorker);
    yield takeLatest(types.LIST_SET_PAGE_SIZE, startPageSizeWorker);
    yield takeLatest(types.LIST_SORT, startSortWorker);
    yield takeLatest(types.LIST_RESET, resetWorker);
    yield takeLatest(types.LIST_RESET_CONFIG, resetConfigWorker);
    yield takeLatest(types.LIST_SAVE, listSaveWorker);
    yield takeLatest(types.LIST_DELETE, listDeleteWorker);
    yield takeLatest(types.LIST_START_EXPORT, listStartExportWorker);
}

function* startInitialQueryWorker(payload: types.ListStartInitialQueryAction) {
    const { resourceName, search, local } = payload;
    const list: types.ListState = yield select(GetDefault);
    const { hidden } = list;
    let request: types.Request;

    if (search != null && hasText(search)) {
        const params = parse(search.substring(1));
        const gridId: string = params['gridId'] as string;
        const grid: types.GridEntity = yield select(GetGrid(gridId));

        const filter = grid.filter;
        if (filter != null) {
            const parser = new Parser(resourceName);
            const expression: Expression = parser.parse(filter);
            yield put(setExpression(expression));
        }

        request = {
            resourceName: grid.resource,
            attributes: parseAttrubutes(grid.resource, grid.attributes),
            order: parseSortQueryStr(grid.order),
            filter,
            pageSpec: {
                offset: 0,
                limit: 25,
            }
        }
    } else {
        request = getInitialRequest(resourceName, hidden);
    }
    yield put(startQuery(request, local));
}

function* startQueryWorker(payload: types.ListQueryAction) {
    const { request, local } = payload;
    if (local) {
        yield queryLocal(request);
    } else {
        yield queryRemote(request);
    }
}

function* startExistingQueryWorker(payload: types.ListExistingQueryAction) {
    const { local } = payload;
    const request: types.Request = yield select(SelectRequest);
    if (local) {
        yield queryLocal(request);
    } else {
        yield queryRemote(request);
    }
}

function* queryRemote(request: types.Request) {
    const { resourceName, attributes, filter, pageSpec: { offset, limit }, order } = request;
    const orderStr: string | null = getSortQueryStr(order);
    const paths = _.keys(attributes);

    const params: any = {
        attributes: paths.length > 0 ? paths.join(',') : undefined,
        filter: filter != null && hasText(filter) ? filter : undefined,
        offset: offset || 0,
        limit: limit,
    }

    if (orderStr !== null) {
        params.order = orderStr;
    }

    let url = `/query/${resourceName}/.search`;

    try {
        let response: ApiResponse = yield call(httpPost, url, {}, {}, params);
        if (response.status == 200) {
            yield put(setResponse(response.json));
        } else {
            yield put(setError(response.json));
        }
    } catch (err: any) {
        yield put(setError(err));
    }
}

function* queryLocal(request: types.Request) {
    const { resourceName, attributes, filter, pageSpec: { offset, limit }, order } = request;

    const entities: Entities = yield select(SelectEntities());

    const transient = _.entries(entities[resourceName])
        .filter(entry => isTransient(entry[0]))
        .map(entry => entry[1]);

    const tuples = transient.map(
        entity => _.entries(attributes).reduce<Dictionary<Value>>((tuple, attr) => {
            const value = entity.values[attr[0]] || entity.attrs[attr[0]];
            if (value !== undefined) {
                tuple[attr[0]] = value;
            }
            return tuple;
        }, {})
    );

    const response: types.Response = {
        totalCount: transient.length,
        attributes: _.keys(attributes),
        limit,
        offset,
        order,
        tuples
    }

    yield put(setResponse(response));
}

function* startPageWorker(payload: types.ListSetPageAction) {
    const list: types.ListState = yield select(GetDefault);
    const { local, request, response } = list;

    if (request !== undefined && response !== undefined) {
        const page: number = getResponsePage(response);

        if (payload.page !== page && request.pageSpec.limit !== null) {
            const { resourceName, attributes } = request;
            const limit = request.pageSpec.limit;
            const offset: number = limit * payload.page;

            yield put(startQuery({
                resourceName,
                attributes,
                order: [],
                pageSpec: {
                    offset: offset,
                    limit: limit,
                }
            }, local));
        }
    }
}

function* startPageSizeWorker(payload: types.ListSetPageSizeAction) {
    const list: types.ListState = yield select(GetDefault);
    const { local, request, response } = list;

    if (request !== undefined && response !== undefined) {
        const size: number | null = response.limit;

        if (payload.size !== size) {
            const { resourceName, attributes } = request;
            const limit = payload.size === -1 ? null : payload.size;
            const offset = 0;

            yield put(startQuery({
                resourceName,
                attributes,
                order: [],
                pageSpec: {
                    offset: offset,
                    limit: limit,
                }
            }, local));
        }
    }
}

function* startSortWorker(payload: types.ListSortAction) {
    const list: types.ListState = yield select(GetDefault);
    const { local, request, response } = list;

    if (request !== undefined && response !== undefined) {
        const { resourceName, attributes, pageSpec, filter } = request;
        const { attr, order } = payload;

        const sort: types.SortCol[] = _.cloneDeep(response.order);
        const index: number = sort.findIndex(sortCol => sortCol.path === attr);

        if (index > -1) {
            if (order === undefined) {
                sort.splice(index, 1);
            } else {
                sort[index].direction = order;
            }
        } else if (order !== null) {
            sort.splice(index, 1, {
                path: attr,
                direction: order
            });
        }

        yield put(startQuery({
            resourceName,
            attributes,
            order: sort,
            pageSpec,
            filter
        }, local));
    }
}

function* startDeleteWorker(payload: types.DeleteStartAction) {
    let base: string = yield select(GetBaseUrl);
    let state: types.ListState = yield select(GetDefault);
    const { local } = state;
    const { resourceName, id } = payload;

    let url = `${base}/api/${resourceName}/${id}`;

    try {
        let response: Response<any> = yield call(httpDeleteOld, url);
        const request: types.Request = state.request!

        yield put(startQuery(request, local));

    } catch (err: any) {
        yield put(setError(err));
    }
}

function* resetWorker(payload: types.ListResetAction) {
    yield put(resetFilter());
}

function* resetConfigWorker(payload: types.ListResetAction) {
    yield put(resetFilter());
    const list: types.ListState = yield select(GetDefault);
    const { request, hidden, local } = list;

    if (request != null) {
        const { resourceName } = request;
        const initialRequest: types.Request = getInitialRequest(resourceName, hidden);
        yield put(startQuery(initialRequest, local));
    }
}

function* listSaveWorker(payload: types.ListSaveAction) {
    const { name } = payload;
    const state: types.ListState = yield select(GetDefault);
    const request: types.Request = state.request!;
    const orderStr: string | null = getSortQueryStr(request.order);

    const params: types.GridEntity = {
        name,
        resource: request.resourceName,
        attributes: _.keys(request.attributes).join(','),
        filter: request.filter,
        order: orderStr !== null ? orderStr : undefined,
    }

    let url = '/_grid';

    try {
        let response: ApiResponse = yield call(httpPost, url, {}, {}, params);
        if (response.status == 201) {
            yield put(addGrid(response.json));
        } else {
            yield put(addSnackbar("Save search error", "grid-save-server-error"));
        }
    } catch (err: any) {
        yield put(addSnackbar("Save search error", "grid-save-unknown-error"));
    }
}

function* listDeleteWorker(payload: types.ListDeleteAction) {
    const { id } = payload;
    let url = '/_grid/{id}';

    try {
        let response: ApiResponse = yield call(httpDelete, url, { id }, {});
        if (response.status == 204) {
            yield put(removeGrid(id));
        } else {
            // TODO: handle error
        }
    } catch (err: any) {
        // TODO: handle error
    }
}

function* listStartExportWorker(payload: types.ListStartExportAction) {
    const { } = payload;
    const request: types.Request = yield select(SelectRequest);

    const { resourceName, attributes, filter, pageSpec: { offset, limit }, order } = request;
    const orderStr: string | null = getSortQueryStr(order);
    const paths = _.keys(attributes);

    const params: any = {
        attributes: paths.length > 0 ? paths.join(',') : undefined,
        filter: filter != null && hasText(filter) ? filter : undefined,
        offset: offset || 0,
        limit: limit,
    }

    if (orderStr !== null) {
        params.order = orderStr;
    }

    let url = `/query/${resourceName}/.json`;

    try {
        let response: ApiResponse = yield call(httpPost, url, {}, {}, params, { asBlob: true });
        if (response.status == 200) {
            downloadBlob(response.blob);
        } else {
            // TODO: handle error
        }
    } catch (err: any) {
        yield put(setError(err));
    }

}

export default function* StartListSagas() {
    yield all([fork(startWatchers)]);
}
